// -----------------------------------------------------------------------------
// Axios-backed production service
// -----------------------------------------------------------------------------
import {
    RegistrationService,
    TokenPair,
    CreateAccountRequest,
    CreateAccountResponse,
    CreateAccountInitRequest,
    CreateAccountInitResponse,
    CompleteSignupRequest,
    CompleteSignupResponse,
    JoinOrganizationRequest,
    JoinOrganizationResponse,
    GetInviteResponse,
    ValidateReferralCodeResponse,
    ClientInvitation,
} from '../types/register';
import { mockClientInvitations } from './clientInvite.mock';
import axios, { AxiosInstance } from 'axios';

/** An organization role an invitation can give. */
export type OrgRole = 'org.member' | 'org.admin' | 'org.owner';

export interface ApiResponse<T> {
    data: T;
    status: string;
}

function path(template: string, params: Record<string, string | number | undefined>) {
    return template.replace(/\{(.*?)\}/g, (_m, k) => {
        const v = params[k];
        if (v === undefined || v === null) throw new Error(`Missing path param: ${k}`);
        return encodeURIComponent(String(v));
    });
}

export interface RegisterHttpServiceOptions {
    baseURL: string;
    axiosInstance?: AxiosInstance;
    onAuthHeader?: (accessToken?: string) => Record<string,string>;
    customRefresh?: (refreshToken: string, client: AxiosInstance) => Promise<TokenPair>;
}

/** The partner's name under the field the pages read (iam sends partner_display_name now). */
const withPartnerName = (inv: ClientInvitation): ClientInvitation => ({ ...inv, partnerName: inv.partnerDisplayName || inv.partnerName });

export class RegisterHttpService implements RegistrationService {
    private client: AxiosInstance;
    private opts: RegisterHttpServiceOptions;

    constructor(opts: RegisterHttpServiceOptions) {
        this.opts = opts;
        this.client = opts.axiosInstance ?? axios.create({
            baseURL: opts.baseURL,
            withCredentials: false,
            headers: { 'Content-Type': 'application/json' },
        });
    }

    async createAccountInit(req: CreateAccountInitRequest): Promise<CreateAccountInitResponse> {
        const { data } = await this.client.post<ApiResponse<CreateAccountInitResponse>>(
            `/v1/iam/account/init`,
            req
        );
        return data.data;
    }

    async createAccount(req: CreateAccountRequest): Promise<CreateAccountResponse> {
        const { data } = await this.client.post<ApiResponse<CreateAccountResponse>>(
            `/v1/iam/account`,
            req
        );
        return data.data;
    }

    async completeSignup(bearerToken: string, req: CompleteSignupRequest): Promise<CompleteSignupResponse> {
        const { data } = await this.client.post<ApiResponse<CompleteSignupResponse>>(
            `/v1/iam/account/signup/complete`,
            req,
            { headers: { Authorization: `Bearer ${bearerToken}` } }
        );
        return data.data;
    }

    /**
     * Read an invite so the page knows what to render.
     *
     * Unauthenticated when the visitor is logged out, which is the normal case
     * for an emailed link. A token, when there is one, only tells the server
     * who is asking — the answer describes the CALLER, never whether the
     * invited address has an account.
     */
    async getInvite(code: string, accessToken?: string): Promise<GetInviteResponse> {
        const url = path(`/v1/iam/orgs/invites/{code}`, { code });
        const { data } = await this.client.get<ApiResponse<GetInviteResponse>>(
            url,
            accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined
        );
        return data.data;
    }

    async joinOrganization(
        req: JoinOrganizationRequest,
        accessToken?: string
    ): Promise<JoinOrganizationResponse> {
        const url = path(`/v1/iam/orgs/invites/{code}`, { code: req.code });
        // The bearer is what lets an EXISTING account join a second
        // organization: the server refuses that path without it, and never
        // touches the password on it.
        const { data } = await this.client.post<ApiResponse<JoinOrganizationResponse>>(
            url,
            req,
            accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined
        );
        return data.data;
    }

    /**
     * Is a partner's referral code usable? Public: no token, on the gateway's
     * no-token allowlist, rate-limited per client IP (429 when exceeded).
     * Reveals only `valid` and the partner's display name.
     */
    async validateReferralCode(code: string): Promise<ValidateReferralCodeResponse> {
        const url = path(`/v1/iam/referral-codes/{code}`, { code });
        const { data } = await this.client.get<ApiResponse<ValidateReferralCodeResponse>>(url);
        return data.data;
    }

    /**
     * A partner's invitation for a new client. Signed in, the caller's email
     * must be the invited one (PERMISSION_DENIED otherwise). Signed out it
     * may be refused (401); the page then asks the person to sign in or up.
     */
    async getClientInvitation(token: string, accessToken?: string): Promise<ClientInvitation> {
        if (mockClientInvitations) return mockClientInvitations.get(token, accessToken);
        const url = path(`/v1/iam/partner-invitations/{token}`, { token });
        const { data } = await this.client.get<ApiResponse<ClientInvitation>>(
            url,
            accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined
        );
        return withPartnerName(data.data);
    }

    /** Accept for an organization the caller holds; it becomes the partner's referred client. */
    async acceptClientInvitation(token: string, organizationId: string, accessToken: string): Promise<ClientInvitation> {
        if (mockClientInvitations) return mockClientInvitations.accept(token, organizationId);
        const url = path(`/v1/iam/partner-invitations/{token}:accept`, { token });
        const { data } = await this.client.post<ApiResponse<ClientInvitation>>(
            url,
            // One id per accept, so a retried click accepts once.
            { organizationId, requestId: crypto.randomUUID() },
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        return withPartnerName(data.data);
    }

    /**
     * Is this organization name free (iam's CheckOrganizationName)? null when
     * it can't say: an older iam or gateway without the route (404), or no
     * answer. Then the submit is the check.
     */
    async checkOrganizationName(name: string, accessToken: string): Promise<{ available: boolean; reason?: string } | null> {
        try {
            const { data } = await this.client.get<ApiResponse<{ available?: boolean; reason?: string }>>(
                `/v1/iam/account/signup/name-check`,
                { params: { name }, headers: { Authorization: `Bearer ${accessToken}` } },
            );
            if ((data as { status?: string })?.status === 'error') return null;
            const body = data?.data ?? {};
            // pbjson leaves a false out: an answer without `available` is "taken".
            return { available: body.available === true, reason: body.reason || undefined };
        } catch {
            return null;
        }
    }

    /**
     * Invite someone to the new organization. Member is what accepting gives
     * anyway, so only Admin and Owner are sent, as the console does.
     */
    async inviteToOrganization(organizationId: string, email: string, role: OrgRole, accessToken: string): Promise<void> {
        const permissionRoles = role === 'org.member' ? [] : [{ serviceId: 'iam-authorization-service', role, withCondition: false }];
        await this.client.post(
            path(`/v1/iam/orgs/{organization_id}/invites`, { organization_id: organizationId }),
            { email, organizationId, permissionRoles },
            { headers: { Authorization: `Bearer ${accessToken}` } },
        );
    }

    /** Start a password reset: iam emails a code if the address has an account, and answers the same either way. */
    async forgetPasswordInit(email: string): Promise<void> {
        await this.client.put(`/v1/iam/users/password-forget/init`, { email });
    }

    /** Finish it: the emailed code and the new password. Signs the account out everywhere else. */
    async forgetPassword(req: { email: string; verificationCode: string; newPassword: string }): Promise<void> {
        await this.client.put(`/v1/iam/users/password-forget`, { mode: 'FORGOT_PASSWORD', ...req });
    }

}
